import { WIZARD_STEPS, type CustomPayRunResponse, type WizardStepKey } from '../types';

/** Custom Pay Run statuses, as the API serializes them (`CustomPayRunStatus.ToString()`). */
const CALCULATED_OR_LATER = ['Calculated', 'ExceptionsPresent', 'ReadyForReview', 'HandedOffToPayrollReview', 'Withdrawn'];
const HANDED_OFF = 'HandedOffToPayrollReview';
const APPROVED = 'Approved';

export interface StepAccess {
  available: boolean;
  /** Why the step cannot be opened yet, in words the preparer can act on. Absent when available. */
  reason?: string;
}

/** Which steps the run's CURRENT state allows. The backend enforces every transition; this only mirrors it so the
 * UI never offers a step the server would refuse. State is `run.status` (Custom Pay Run) and `run.payrollRunStatus`
 * (the live Payroll Review status of the handed-off run). */
export function stepAccess(run: CustomPayRunResponse): Record<WizardStepKey, StepAccess> {
  if (run.status === 'Discarded') {
    // A discarded run is read-only history: only its (read-only) first step opens.
    const closed = { available: false, reason: 'This run was discarded, so there is nothing further to review.' };
    return { 'custom-payroll': { available: true }, 'review-payroll': closed, 'pending-payroll': closed, 'approved-payroll': closed };
  }
  // EPN-241: a withdrawn run keeps its review steps readable (the approver and the preparer both see what happened) but can never reach approval.
  const withdrawn = run.status === 'Withdrawn';
  const handedOff = run.status === HANDED_OFF || withdrawn;
  const approved = handedOff && run.payrollRunStatus === APPROVED;
  return {
    'custom-payroll': { available: true },
    'review-payroll': CALCULATED_OR_LATER.includes(run.status)
      ? { available: true }
      : { available: false, reason: 'Review Payroll opens once the run has been calculated. Include at least one employee, then choose Proceed to Review Payroll.' },
    'pending-payroll': handedOff
      ? { available: true }
      : { available: false, reason: 'Pending Payroll opens once the run has been sent for approval from Review Payroll.' },
    'approved-payroll': approved
      ? { available: true }
      : {
          available: false,
          reason:
            withdrawn
              ? 'This run was withdrawn by the preparer before it was approved, so it will not reach Approved Payroll.'
              : handedOff && run.payrollRunStatus === 'Rejected'
              ? 'This run was rejected in Payroll Review and is final, so it will not reach Approved Payroll.'
              : 'Approved Payroll opens once the run has been approved in Pending Payroll.',
        },
  };
}

/** The last step the run's state allows — where a resumed run should open. */
export function furthestAvailableStep(run: CustomPayRunResponse): WizardStepKey {
  const access = stepAccess(run);
  let furthest: WizardStepKey = 'custom-payroll';
  for (const step of WIZARD_STEPS) {
    if (access[step.key].available) furthest = step.key;
  }
  return furthest;
}

/** Steps whose work is done, for the stepper's green check. */
export function completedSteps(run: CustomPayRunResponse): Set<WizardStepKey> {
  const access = stepAccess(run);
  const done = new Set<WizardStepKey>();
  if (access['review-payroll'].available) done.add('custom-payroll');
  if (access['pending-payroll'].available) done.add('review-payroll');
  if (access['approved-payroll'].available) {
    done.add('pending-payroll');
    done.add('approved-payroll');
  }
  return done;
}
